import React, { useEffect, useRef, useState } from 'react';
import {
  FiSun, FiMoon, FiGrid, FiChevronLeft, FiChevronRight, FiCalendar,
  FiPlus, FiTrash2, FiEdit2, FiCheck, FiX,
} from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import { useLocale } from '../context/LocaleContext';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import * as safeService from '../services/safeService';
import SafeCountModal from './SafeCountModal';
import '../gold-purchase.css';

/* Helpers */
function money(n) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
}
function toISODate(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODate(dt);
}
function editVal(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) && n !== 0 ? String(v) : '';
}
function timeHM(unixSeconds) {
  const n = parseInt(unixSeconds, 10);
  if (!Number.isFinite(n) || n <= 0) return '';
  return new Date(n * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
const CURR = ['usd', 'srd', 'eur'];

export default function SafeDashboard() {
  const { user, logout } = useAuth();
  const { t, locale, setLocale } = useLocale();
  const { theme, toggleTheme } = useTheme();
  const { goToPicker } = useModule();

  const [date, setDate] = useState(() => toISODate(new Date()));
  const [transactions, setTransactions] = useState([]);
  const [opening, setOpening] = useState({ usd: 0, srd: 0, eur: 0 });
  const [saldo, setSaldo] = useState({ usd: 0, srd: 0, eur: 0 });

  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCount, setShowCount] = useState(false);

  const lastVersion = useRef('');
  const pendingSync = useRef(false);
  const dateRef = useRef(date); dateRef.current = date;
  const draftRef = useRef(draft); draftRef.current = draft;

  const applyDay = (data) => {
    setTransactions(data.transactions || []);
    setOpening(data.opening || { usd: 0, srd: 0, eur: 0 });
    setSaldo(data.saldo || { usd: 0, srd: 0, eur: 0 });
  };

  const loadDay = async (iso = dateRef.current) => {
    const res = await safeService.getDay(iso);
    if (res.success && res.data) {
      applyDay(res.data);
      const v = await safeService.getDayVersion(iso);
      if (v.success && v.data) lastVersion.current = v.data.version;
    }
  };

  useEffect(() => {
    loadDay(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Live sync every 4s (any change anywhere affects the running saldo).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      const v = await safeService.getDayVersion(dateRef.current);
      if (cancelled || !v.success || !v.data) return;
      if (v.data.version === lastVersion.current) return;
      if (draftRef.current) pendingSync.current = true;
      else await loadDay(dateRef.current);
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!draft && pendingSync.current) {
      pendingSync.current = false;
      loadDay(dateRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  /* Draft row */
  const startAdd = () => setDraft({ id: null, type: 'add', description: '', usd: '', srd: '', eur: '' });
  const startEdit = (tx) => setDraft({ id: tx.id, type: tx.type, description: tx.description || '', usd: tx.amount_usd, srd: tx.amount_srd, eur: tx.amount_eur });
  const setDraftField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const cancelDraft = () => setDraft(null);

  const confirmDraft = async () => {
    if (!draft || saving) return;
    const usd = parseFloat(draft.usd) || 0;
    const srd = parseFloat(draft.srd) || 0;
    const eur = parseFloat(draft.eur) || 0;
    if (usd === 0 && srd === 0 && eur === 0) { alert(t('safe_need_amount')); return; }
    setSaving(true);
    try {
      let res;
      if (draft.id === null) {
        res = await safeService.addTransaction(date, draft.type, draft.description.trim(), { usd, srd, eur });
      } else {
        res = await safeService.updateTransaction(draft.id, { type: draft.type, description: draft.description.trim(), usd, srd, eur });
      }
      if (res.success) { setDraft(null); await loadDay(date); }
      else alert(res.message || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const deleteTx = async (id) => {
    if (!window.confirm(t('delete_tx_confirm'))) return;
    const res = await safeService.deleteTransaction(id);
    if (res.success) await loadDay(date);
    else alert(res.message || 'Failed to delete transaction');
  };

  /* Date nav */
  const goPrev = () => setDate((d) => addDays(d, -1));
  const goNext = () => setDate((d) => addDays(d, 1));
  const goToday = () => setDate(toISODate(new Date()));
  const isToday = date === toISODate(new Date());

  const hasOpening = (opening.usd || 0) !== 0 || (opening.srd || 0) !== 0 || (opening.eur || 0) !== 0;

  return (
    <div className="layout">
      <header className="app-header">
        <div className="brand">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="Logo" />
          <h1 className="app-title">{t('brand_title')}</h1>
        </div>
        <div className="app-actions">
          <button className="btn btn-ghost btn-icon" onClick={goToPicker} title={t('back_to_modules')} aria-label={t('back_to_modules')}>
            <FiGrid />
          </button>
          <button className="btn btn-ghost btn-icon" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} aria-label="Toggle theme">
            {theme === 'dark' ? <FiSun /> : <FiMoon />}
          </button>
          <select className="lang-select" value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language" title="Language">
            <option value="en">English</option>
            <option value="pt-BR">Português (BR)</option>
          </select>
          <span className="muted">{t('signed_in_as')} {user?.username}</span>
          <button className="btn btn-ghost" onClick={logout}>{t('logout')}</button>
        </div>
      </header>

      <main className="content gp-content">
        {/* Date navigation */}
        <div className="gp-datebar">
          <button className="btn btn-ghost btn-icon" onClick={goPrev} title={t('prev_day')}><FiChevronLeft /></button>
          <div className="gp-date-display">
            <FiCalendar />
            <input
              type="date" className="gp-date-input" value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
            />
          </div>
          <button className="btn btn-ghost btn-icon" onClick={goNext} title={t('next_day')}><FiChevronRight /></button>
          {!isToday && <button className="btn btn-ghost btn-sm" onClick={goToday}>{t('today')}</button>}
          <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setShowCount(true)}>
            {t('safe_count_title')}
          </button>
        </div>

        {/* Centered saldo */}
        <div className="safe-saldo-wrap">
          <div className="safe-saldo-label">{t('safe_saldo')}</div>
          <div className="safe-saldo-grid">
            {CURR.map((c) => {
              const v = saldo[c] || 0;
              return (
                <div className="safe-saldo-item" key={c}>
                  <span className="safe-saldo-code">{c.toUpperCase()}</span>
                  <span className={'safe-saldo-val ' + (v >= 0 ? 'pos' : 'neg')}>
                    {v < 0 ? '−' : ''}{money(Math.abs(v))}
                  </span>
                </div>
              );
            })}
          </div>
          {hasOpening && (
            <div className="safe-opening muted small">
              {t('safe_opening')}: {CURR.map((c) => `${c.toUpperCase()} ${money(opening[c] || 0)}`).join('   ')}
            </div>
          )}
        </div>

        {/* Transactions */}
        <div className="gp-table-wrap">
          <table className="gp-table">
            <thead>
              <tr>
                <th style={{ width: '70px' }}>{t('kassa_time')}</th>
                <th style={{ width: '120px' }}>{t('type')}</th>
                <th>{t('description')}</th>
                <th className="gp-num gp-paid-col">USD</th>
                <th className="gp-num gp-paid-col">SRD</th>
                <th className="gp-num gp-paid-col">EUR</th>
                <th className="gp-actions-col"></th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 && !draft && (
                <tr><td colSpan={7} className="gp-empty muted">{t('no_transactions')}</td></tr>
              )}
              {transactions.map((tx) => {
                if (draft && draft.id === tx.id) {
                  return <SafeDraftRow key={tx.id} draft={draft} saving={saving} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />;
                }
                const isAdd = tx.type === 'add';
                const sign = isAdd ? '+' : '−';
                return (
                  <tr key={tx.id}>
                    <td className="gp-ro kassa-time">{timeHM(tx.created_at)}</td>
                    <td>
                      <span className={'kassa-pill ' + (isAdd ? 'pos' : 'neg')}>{isAdd ? t('safe_add') : t('safe_subtract')}</span>
                    </td>
                    <td className="kassa-desc">{tx.description || '—'}</td>
                    {CURR.map((c) => {
                      const v = parseFloat(tx['amount_' + c]) || 0;
                      const cls = v === 0 ? 'gp-ro' : (isAdd ? 'gp-gold-pos' : 'gp-gold-neg');
                      return <td key={c} className={'gp-num gp-paid-col ' + cls}>{v === 0 ? '—' : `${sign} ${money(v)}`}</td>;
                    })}
                    <td className="gp-actions-col">
                      <div className="gp-row-actions">
                        <button className="btn btn-ghost btn-icon" title={t('edit')} onClick={() => startEdit(tx)} disabled={!!draft}><FiEdit2 /></button>
                        <button className="btn btn-ghost btn-icon" title={t('delete')} onClick={() => deleteTx(tx.id)} disabled={!!draft}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {draft && draft.id === null && (
                <SafeDraftRow draft={draft} saving={saving} onField={setDraftField} onConfirm={confirmDraft} onCancel={cancelDraft} t={t} />
              )}
            </tbody>
          </table>

          <button className="btn btn-primary gp-add-btn" onClick={startAdd} disabled={!!draft}>
            <FiPlus style={{ marginRight: '6px' }} /> {t('safe_add_line')}
          </button>
        </div>
      </main>

      {showCount && (
        <SafeCountModal
          date={date}
          saldo={saldo}
          t={t}
          onClose={() => setShowCount(false)}
          onSaved={() => loadDay(date)}
        />
      )}
    </div>
  );
}

function SafeDraftRow({ draft, saving, onField, onConfirm, onCancel, t }) {
  const onKey = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); onConfirm(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <tr className="gp-draft-row" onKeyDown={onKey}>
      <td className="gp-ro kassa-time muted">—</td>
      <td>
        <select className="gp-cell" value={draft.type} onChange={(e) => onField('type', e.target.value)}>
          <option value="add">{t('safe_add')}</option>
          <option value="subtract">{t('safe_subtract')}</option>
        </select>
      </td>
      <td>
        <input className="gp-cell kassa-desc-cell" type="text" autoFocus
          value={draft.description} onChange={(e) => onField('description', e.target.value)} placeholder={t('description_placeholder')} />
      </td>
      {CURR.map((c) => (
        <td key={c} className="gp-num gp-paid-col">
          <input className="gp-cell" type="number" step="0.01" inputMode="decimal"
            value={editVal(draft[c])} onChange={(e) => onField(c, e.target.value)} placeholder="0.00" />
        </td>
      ))}
      <td className="gp-actions-col">
        <div className="gp-row-actions">
          <button className="btn btn-primary btn-icon" title={t('kassa_confirm')} onClick={onConfirm} disabled={saving}><FiCheck /></button>
          <button className="btn btn-ghost btn-icon" title={t('cancel')} onClick={onCancel} disabled={saving}><FiX /></button>
        </div>
      </td>
    </tr>
  );
}
